<template>
  <FreePageLayout>
    <template #header>
      <FreeHeader class="p-4" is-centered>
        <template #leftAction>
          <FreeBackButton @click="router.push({ name: 'FavoritesPage' })" />
        </template>
        <template #default>
          <h2 class="text-center text-lg font-semibold">
            {{ title }}
          </h2>
        </template>
      </FreeHeader>
    </template>
    <template #default>
      <div class="pb-20">
        <FavoritesListShimmer v-if="isAuthorized && isLoading" />
        <FavoritesListView
          v-else-if="isAuthorized && selectedGroup"
          :groups="[selectedGroup]"
          :dict="watchlistDict"
          hide-title
          @remove="removeFavorite"
          @openItem="handleOpenItem"
        />
      </div>
    </template>
  </FreePageLayout>
</template>

<script setup lang="ts">
  import { computed, watch } from 'vue';
  import { useRouter } from 'vue-router';
  import { useI18n } from 'vue-i18n';
  import { FreeBackButton, FreeHeader, FreePageLayout } from 'freedom-shared';
  import type { appLocale } from 'shared';

  import { FavoritesListView, FavoritesListShimmer } from '@/widgets/favorites';

  import {
    routeTitleFromEndpoints,
    groupByDirection,
    useWatchlist,
    navigateFromWatchlistItem,
    type WatchlistItem,
  } from '@/features/favorites';

  import { amplitudeEventsLib } from '@/shared/lib';

  type Locale = appLocale.config.Locale;

  const props = defineProps<{
    routeKey: string;
  }>();

  const router = useRouter();
  const {
    items,
    watchlistDict,
    isLoading,
    toggleSearch,
    toggleOffer,
    isAuthorized,
  } = useWatchlist();

  const activeItems = computed(() =>
    items.value.filter(item => item.active !== false),
  );

  const selectedGroup = computed(() =>
    groupByDirection(activeItems.value).find(
      group => group.direction.key === props.routeKey,
    ),
  );

  const { locale, t } = useI18n();

  const title = computed(() => {
    if (!selectedGroup.value) return t('favoritesPage.title');
    const { dep, arr } = selectedGroup.value.direction;
    return routeTitleFromEndpoints(
      dep,
      arr,
      watchlistDict.value,
      locale.value as Locale,
    );
  });

  let pageOpenEventFired = false;

  watch(
    [isLoading, selectedGroup, isAuthorized],
    ([loading, group, authorized]) => {
      if ((authorized && loading) || !group || pageOpenEventFired) {
        return;
      }
      pageOpenEventFired = true;

      amplitudeEventsLib.favoritesDirectionOpen({
        search_count: group.savedSearches.length,
        offer_count: group.savedTickets.length,
      });
    },
    { immediate: true },
  );

  function removeFavorite(item: (typeof items.value)[number]) {
    if (item.hash) {
      toggleOffer(
        item.hash,
        item.search_query,
        item.initial_price,
        item.currency,
      );
      return;
    }

    toggleSearch(item.search_query, item.initial_price, item.currency);
  }

  watch(
    () => selectedGroup.value,
    group => {
      if ((!isAuthorized.value || !isLoading.value) && group === undefined) {
        router.push({ name: 'FavoritesPage' });
      }
    },
    { immediate: true },
  );

  function handleOpenItem(item: WatchlistItem) {
    if (!selectedGroup.value) {
      navigateFromWatchlistItem(router, item);
      return;
    }
    const allItems = [
      ...selectedGroup.value.savedSearches,
      ...selectedGroup.value.savedTickets,
    ];
    const position = allItems.findIndex(i => i.id === item.id);
    if (item.hash !== null) {
      amplitudeEventsLib.favoritesOfferClick(position, 'direction_cards');
    } else {
      amplitudeEventsLib.favoritesSearchClick(position, 'direction_cards');
    }
    navigateFromWatchlistItem(router, item);
  }
</script>
